import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { ComposedChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from 'recharts';
import { DealInsightTimelineMode, Activities, DealTimeLineResponse } from 'src/types/dealInsights';
import moment from 'moment';
import styles from './styles.module.scss';
import { find, filter, flatMap, get, groupBy, map, max, reverse, size, sumBy, trim, isEmpty, some } from 'lodash';
import TabPanel from 'src/pages/dealInsights/components/tabPanel';
import { useDealTimeline } from 'src/query/dealInsights/dealInsights';
import { dealInsightTimelineTabs, activityGraphLabel, activityIconMap } from 'src/utils/dealInsights.helper';
import { TextClamp } from 'src/pages/dealInsights/components/textClamp';

interface Props {
  dealId: string
}

const emptyImg = '/images/dealInsights/emptyDeals.svg';

// Components
const ActivitiesStack = (props: any) => {
  const { cx: x, cy: y, payload, graphData, height: parentHeight, minActivityWidth } = props;
  const activity = find(graphData, (item) => item.date === payload.date);
  if (!activity || size(activity.activities) === 0) return null;

  const filteredActivities = filter(activity.activities, (act: any) => act.type !== 'STAGE_CHANGE');
  if (size(filteredActivities) === 0) return null;

  // Method
  const isRightAlign = (n: number) => n % 2 === 0; // even number only on right side

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-(minActivityWidth / 2)} y={-(parentHeight + 10)} width={minActivityWidth} height={parentHeight} style={{ overflow: 'visible' }}>
        <div className={styles.activitiesContainer} >
          {map(filteredActivities, (act: any, i: number) => (<div key={i} className={cx(styles.content, {
            [styles.rightAlign]: isRightAlign(i),
            [styles.leftAlign]: !isRightAlign(i),
          })}>

            {act.count > 1 ?
              <div className={styles.countContainer}>
                <div className={styles.icon}>
                  <i className={activityIconMap[act.type]}></i>
                </div>
                <span className={styles.dot}></span>
                <p> {act.count}</p>
              </div> : <div className={styles.iconContainer}>
                <i className={activityIconMap[act.type]}></i>
              </div>}
          </div>
          ))}
        </div>
      </foreignObject>
    </g>
  );
};

const DateLabels = (props: any) => {
  const { x, y, payload, graphData } = props;
  const activity = find(graphData, (item) => item.date === payload.value)
  const hasActivitiesStage = some(activity.activities, (act: any) => act.type === 'STAGE_CHANGE');;

  return (
    <text x={x} y={y} dy={20} className={styles.dateLabelContainer} textAnchor="middle">
      <tspan className={cx(styles.label, { [styles.hasStage]: hasActivitiesStage })}>
        {!isEmpty(activity.date) ? moment(activity.date).format("DD MMM'YY") : ' '}
      </tspan> {/* Second line */}
    </text>
  )
}

const StageLabels = (props: any,) => {
  const { x, y, height, payload, graphData, minActivityWidth } = props;
  const activity = graphData.find((d: any) => d.date === payload.value);

  if (!activity || size(activity.activities) === 0) return null;

  const activityStages = filter(activity.activities, (act: any) => act.type === 'STAGE_CHANGE')?.[0]?.subject || []; // only deal stage
  if (size(activityStages) === 0) return null;

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-(minActivityWidth / 2)} y={0} width={minActivityWidth} height={height} style={{ overflow: 'visible' }}>
        <div className={styles.stagesContainer} >
          {map(activityStages, (stage: string, i: number) => (
            <TextClamp key={i} text={stage} classnames={styles.milestonePill} tooltipContent={stage} />
          ))}
        </div>
      </foreignObject>
    </g>
  );
};


const NoResultsFound = ({ msg, height }: { msg: string, height: number }) => (
  <div className={styles.emptyState} style={{ height }}>
    <img src={emptyImg} alt="No results" className={styles.emptyStateImg} />
    <p className={styles.emptyStateMsg}>{msg}</p>
  </div>
);

export const DealTimeline = ({ dealId }: Props) => {
  const [selectedMode, setSelectedMode] = useState<DealInsightTimelineMode>(DealInsightTimelineMode.BOTH);
  const [graphData, setGraphData] = useState<any[]>([]);
  const [groupedActivities, setGroupedActivities] = useState<any[]>([]);

  const [maxYValue, setMaxYValue] = useState<number>(200); // y axix max value (predictions)
  const [chartHeight, setChartHeight] = useState<number>(300); // chart height
  const [stagesHeight, setStagesHeight] = useState<number>(20);
  const minActivityWidth = 120; // graph width row - AcitivitesStack

  // query
  const { data: dealTimeline, isLoading: isloadingTimeline, isRefetching: isTimelineRefetching } = useDealTimeline(dealId); // deal timeline api

  // Methods
  useEffect(() => {
    setGraphData(getTheGraphData(dealTimeline));
  }, [dealTimeline]);

  useEffect(() => {
    if (size(graphData) === 0) return;
    setGroupedActivities(getTheGroupedActivites());
    // setMaxYValue(Math.max(...graphData.map(d => d.score || 0))); // get the max score to get the max y
    measureTheChartHeights();
  }, [graphData]);

  const getTheGraphData = (activities: DealTimeLineResponse) => {
    if (!activities?.timelines) return [];

    const activityRecords = size(activities.timelines) > 0 ? reverse([...activities.timelines]) : [];
    const normalizedData = size(activityRecords) === 1 ? // normailze the data
      [activityRecords[0],
      {
        date: '',
        activities: [],
      }] : activityRecords;

    return normalizedData;
  }

  // grouped activities
  const getTheGroupedActivites = () => { // combine all the acitivity
    if (!dealTimeline?.timelines) return [];

    const allActivities = flatMap(dealTimeline?.timelines, item => item.activities || []);
    const grouped = groupBy(allActivities, 'type');
    return map(grouped, (group, type) => ({
      type,
      title: activityGraphLabel[type] || '',
      count: sumBy(group, 'count')
    }));;
  };

  // chart measure (stages)
  const measureTheChartHeights = () => {
    const stageLabelHeight = 20; // stage label height
    const maxStage = max(map(graphData, item => {
      const stageActivity = find(item.activities, (act: any) => act.type === 'STAGE_CHANGE');
      return size(get(stageActivity, 'subject', []));
    })) || 0;

    setStagesHeight(maxStage > 0 ? maxStage * (stageLabelHeight + 6) : 6);
  }

  return (
    <>
      <div className={styles.timelineContainer}>
        <div className={styles.header}>
          <h3 className={styles.title}>Deal Timeline</h3>
          <div className={styles.modeToggle}>
            {size(graphData) > 0 && <TabPanel
              listOfTabs={dealInsightTimelineTabs}
              selected={selectedMode}
              classnames={styles.tabPanel}
              onChange={(val) => setSelectedMode(val as DealInsightTimelineMode)}
            />}
          </div>
        </div>

        {isloadingTimeline || isTimelineRefetching ? <p style={{ height: chartHeight + stagesHeight }} className={styles.loading}>
          Loading...
        </p> : <>
          {size(graphData) > 0 ? <> <div className={styles.chartArea}>
            <div className={styles.graphContainer}>
              <div style={{
                width: `${graphData.length * minActivityWidth}px`, // calculate the width based on the number of items in graph
              }}>
                <ResponsiveContainer width="100%" height={chartHeight + stagesHeight}>
                  <ComposedChart data={graphData} margin={{ top: 20, right: 70, left: 60, bottom: 0 }}>

                    <defs>
                      <linearGradient id="areaChartShades" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="15%" stopColor="#E51058" stopOpacity={0.2} />
                        <stop offset="85%" stopColor="#E51058" stopOpacity={0} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid horizontal={false} vertical={true} strokeDasharray="4 4" stroke="#F7F7F7" />

                    <XAxis
                      dataKey="date"
                      xAxisId="dates"
                      axisLine={false}
                      tickLine={false}
                      height={70}
                      interval={0}
                      tick={(props) => <DateLabels {...props} graphData={graphData} />}
                    />

                    <XAxis
                      dataKey="date"
                      xAxisId="stages"
                      axisLine={{ stroke: '#DBDBDB', strokeWidth: 1 }}
                      tickLine={false}
                      height={stagesHeight}
                      interval={0}
                      tick={(props) => <StageLabels {...props} graphData={graphData} minActivityWidth={130} />}
                    />

                    <YAxis hide domain={[0, maxYValue > 0 ? maxYValue * 1.1 : 100]} />

                    {/* {selectedMode !== DealInsightTimelineMode.ACTIVITY && ( // score based
                      <Area
                        xAxisId="dates"
                        type="monotone"
                        dataKey="score"
                        stroke="#E51058"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#areaChartShades)"
                      />
                    )} */}

                    <Line
                      xAxisId="dates"
                      type="monotone"
                      dataKey={() => 1} // constant baseline
                      stroke="transparent" // IMPORTANT FIX
                      activeDot={false}
                      isAnimationActive={false}
                      dot={(props) => <ActivitiesStack {...props} graphData={graphData} minActivityWidth={minActivityWidth} />}
                    />

                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

            {size(groupedActivities) > 0 && <div className={styles.groupActivities}>
              {map(groupedActivities, (activity, i) => (
                <div key={i} className={styles.pill}>
                  <i className={activityIconMap[activity.type]} />
                  <div>
                    <p className={styles.label}>{activity.title}:</p>
                    <p className={styles.count}>{activity.count}</p>
                  </div>
                </div>
              ))}
            </div>}  </> : <NoResultsFound height={chartHeight} msg="No activities found in graph" />}
        </>}
      </div>
    </>
  );
};

export default DealTimeline;

